import { useMutation, useQueryClient } from '@tanstack/react-query';
import { connectionsApi } from '../api';
import { toast } from 'sonner';
import { queryKeys } from './useConnections';

type FollowableObjectType = 'entity' | 'item' | 'initiative' | 'content';

function getQueryKeys(objectType: FollowableObjectType, objectIdStr: string) {
  switch (objectType) {
    case 'entity':
      return {
        prefix: ['entities', objectIdStr],
        single: ['entities', objectIdStr],
      };
    case 'item':
      return {
        prefix: ['items', objectIdStr],
        single: ['item', objectIdStr],
      };
    case 'initiative':
      return {
        prefix: ['initiatives', 'detail', Number(objectIdStr)],
        single: ['initiatives', 'detail', Number(objectIdStr)],
      };
    case 'content':
      return {
        prefix: ['content', objectIdStr],
        single: ['content', objectIdStr],
      };
  }
}

/**
 * Hook to follow an entity, item, or initiative
 */
export function useFollow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      objectId,
      objectType,
    }: {
      objectId: number;
      objectType: FollowableObjectType;
    }) => connectionsApi.follow(objectId, objectType),
    onMutate: async (variables) => {
      const objectIdStr = variables.objectId.toString();
      const keys = getQueryKeys(variables.objectType, objectIdStr);

      // Cancel outgoing refetches for all queries with this entity/item/initiative
      await queryClient.cancelQueries({ queryKey: keys.prefix });
      await queryClient.cancelQueries({ queryKey: keys.single });

      // Snapshot all matching queries
      const previousQueries = queryClient.getQueriesData({
        queryKey: keys.prefix,
      });
      const previousSingleQuery = queryClient.getQueryData(keys.single);

      // Optimistically update all matching queries
      queryClient.setQueriesData(
        {
          queryKey: keys.prefix,
        },
        (old: any) => (old ? { ...old, following: true } : old)
      );

      // Also update the singular query key for detail views
      queryClient.setQueryData(keys.single, (old: any) =>
        old ? { ...old, following: true } : old
      );

      return { previousQueries, previousSingleQuery, singleQueryKey: keys.single };
    },
    onSuccess: (_data, variables) => {
      // Invalidate connections queries
      queryClient.invalidateQueries({ queryKey: queryKeys.connections });
      // Also invalidate initiative list queries so they reflect updated follow status
      if (variables.objectType === 'initiative') {
        queryClient.invalidateQueries({ queryKey: ['initiatives', 'list'] });
      }
    },
    onError: (error: any, _variables, context) => {
      // Rollback all queries
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      // Rollback singular query
      if (context?.previousSingleQuery && context?.singleQueryKey) {
        queryClient.setQueryData(context.singleQueryKey, context.previousSingleQuery);
      }

      console.error('Failed to follow:', error);
      toast.error(error?.message || 'Failed to follow');
    },
  });
}

/**
 * Hook to unfollow an entity, item, or initiative
 */
export function useUnfollow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      objectId,
      objectType,
    }: {
      objectId: number;
      objectType: FollowableObjectType;
    }) => connectionsApi.unfollow(objectId, objectType),
    onMutate: async (variables) => {
      const objectIdStr = variables.objectId.toString();
      const keys = getQueryKeys(variables.objectType, objectIdStr);

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: keys.prefix });
      await queryClient.cancelQueries({ queryKey: keys.single });

      // Snapshot all matching queries
      const previousQueries = queryClient.getQueriesData({
        queryKey: keys.prefix,
      });
      const previousSingleQuery = queryClient.getQueryData(keys.single);

      // Optimistically update all matching queries
      queryClient.setQueriesData(
        {
          queryKey: keys.prefix,
        },
        (old: any) => (old ? { ...old, following: false } : old)
      );

      // Also update the singular query key for detail views
      queryClient.setQueryData(keys.single, (old: any) =>
        old ? { ...old, following: false } : old
      );

      return { previousQueries, previousSingleQuery, singleQueryKey: keys.single };
    },
    onSuccess: (_data, variables) => {
      // Invalidate connections queries
      queryClient.invalidateQueries({ queryKey: queryKeys.connections });
      // Also invalidate initiative list queries
      if (variables.objectType === 'initiative') {
        queryClient.invalidateQueries({ queryKey: ['initiatives', 'list'] });
      }
    },
    onError: (error: any, _variables, context) => {
      // Rollback all queries
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      // Rollback singular query
      if (context?.previousSingleQuery && context?.singleQueryKey) {
        queryClient.setQueryData(context.singleQueryKey, context.previousSingleQuery);
      }

      console.error('Failed to unfollow:', error);
      toast.error(error?.message || 'Failed to unfollow');
    },
  });
}

/**
 * Hook to check if user is following an object and toggle follow status
 */
export function useFollowToggle(objectId: number, objectType: FollowableObjectType) {
  const followMutation = useFollow();
  const unfollowMutation = useUnfollow();

  const toggleFollow = (following: boolean) => {
    if (following) {
      unfollowMutation.mutate({ objectId, objectType });
    } else {
      followMutation.mutate({ objectId, objectType });
    }
  };

  return {
    toggleFollow,
    isLoading: followMutation.isPending || unfollowMutation.isPending,
  };
}
